import Image from 'components/Common/Image/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { isLEStaffOrAdmin } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { deletePlaceCache } from 'tripPlanner/api'
import { ChatMap } from 'tripPlanner/components/ChatMap'
import type { ChatMapMarker } from 'tripPlanner/components/ChatMap/types'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import {
  useGetPlaceAreas,
  useGetPlaceBestTimeToVisit,
  useGetPlaceInsiderTips,
  useGetPlaceOverview,
  useGetPlaceWhatToLove,
} from 'tripPlanner/hooks/api/chat'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Clickable from 'components/Common/Clickable'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import Table from 'LuxKit/Tables/Table'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import { take } from 'lib/array/arrayUtils'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import DrawerAskAIFooter from 'tripPlanner/components/DrawerAskAI/DrawerAskAIFooter'

const ContentWrapper = styled(Group)`
  margin: ${rem(24)} 0;
  padding: 0 ${rem(24)};
  width: ${rem(700)};
  max-width: 100%;
  box-sizing: border-box;
`

const MapWrapper = styled.div`
  width: 100%;
  height: ${rem(300)};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
`

const RoundedTable = styled(Table)`
  border-radius: ${props => props.theme.borderRadius.L};
`

const TipCard = styled.div`
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.M};
`

const ImageThumbnail = styled.button`
  width: ${rem(80)};
  height: ${rem(80)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  border: none;
  padding: 0;
  cursor: pointer;
`

const UrlImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ModalUrlImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
`

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 50% 50%;
  gap: ${rem(4)};
  width: 100%;
  height: ${rem(340)};
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;

  > :first-child {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }
`

export const GalleryButtons = styled(Group)`
  pointer-events: auto;
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(16)};
`

export const GridImageClickable = styled(Clickable)`
  width: 100%;
  height: 100%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }
const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }

interface Props {
  isOpen: boolean
  onDismiss: () => void
  onAfterClose?: () => void
  googlePlaceId: string
  placeName: string
  countryName?: string
  stopId?: number
  onDeleteStop?: (stopId: number) => void
}

function TripPlanDetailDrawer({ isOpen, onDismiss, onAfterClose, googlePlaceId, placeName, countryName, stopId, onDeleteStop }: Props) {
  const intl = useIntl()

  const failedToLoadText = intl.formatMessage({
    id: 'FA17vx7NLkSZhFIBlGQD',
    defaultMessage: 'Failed to load additional information.',
    description: 'Error text when all trip plan detail sections fail to load',
  })
  const retryLabel = intl.formatMessage({
    id: 'U-ZcfakqpLsj2dlV-u4M',
    defaultMessage: 'Retry',
    description: 'Button label to retry loading trip plan detail sections',
  })
  const cityInCountryText = intl.formatMessage(
    {
      id: 'IrCYQGrjDZfcICrhlbRR',
      defaultMessage: 'City in {countryName}',
      description: 'Subtitle showing which country a city belongs to in trip plan detail',
    },
    { countryName },
  )
  const viewGalleryLabel = intl.formatMessage({
    id: 'VR9gBhLOmH6uKdz4K4GZ',
    defaultMessage: 'View gallery',
    description: 'Button label to open image gallery lightbox in trip plan detail',
  })
  const bestTimeToVisitHeading = intl.formatMessage({
    id: 'S798osdLcKcFQLKzPVwr',
    defaultMessage: 'Best Time to Visit',
    description: 'Heading for best time to visit section in trip plan detail',
  })
  const seasonHeader = intl.formatMessage({
    id: 'oZpAAoObVMOQuOdqY-Wl',
    defaultMessage: 'Season',
    description: 'Table header for season column in best time to visit section',
  })
  const weatherHeader = intl.formatMessage({
    id: 'JBSlAQSIQQaoKtdei1de',
    defaultMessage: 'Weather',
    description: 'Table header for weather column in best time to visit section',
  })
  const whyVisitHeader = intl.formatMessage({
    id: 'xnx6SqiQiV6CBZpeEg1R',
    defaultMessage: 'Why Visit',
    description: 'Table header for why visit column in best time to visit section',
  })
  const whatYoullLoveHeading = intl.formatMessage({
    id: 'neot_xLo154w2TBir6Y4',
    defaultMessage: 'What you’ll love',
    description: 'Heading for what to love section in trip plan detail',
  })
  const topAreasHeading = intl.formatMessage({
    id: '5qveZGlLczJjuvPraVc7',
    defaultMessage: 'Top areas to visit',
    description: 'Heading for top areas to visit section in trip plan detail',
  })
  const insiderTipsHeading = intl.formatMessage({
    id: 'SmqFMCdvG9iJmtM4tF3s',
    defaultMessage: 'Insider Tips',
    description: 'Heading for insider tips section in trip plan detail',
  })
  const locationHeading = intl.formatMessage({
    id: 'YrfaTmB2ca_D95RIXBwK',
    defaultMessage: 'Location',
    description: 'Heading for location map section in trip plan detail',
  })
  const imagePreviewTitle = intl.formatMessage({
    id: 'RushD1t8TY9paPWWptos',
    defaultMessage: 'Image Preview',
    description: 'Modal title for image preview in trip plan detail in the trip planner AI chat',
  })
  const cacheClearedForPlaceAlert = intl.formatMessage({
    id: 'F4GrAyrg04P-GiB6KLJ0',
    defaultMessage: 'Cache cleared for this place',
    description: 'Admin alert after clearing cache for a specific place',
  })
  const confirmDeleteAllCacheText = intl.formatMessage({
    id: 'VsJFtUOefZhuf8DYNTUk',
    defaultMessage: 'Are you sure you want to delete ALL cached place data? This cannot be undone.',
    description: 'Admin confirmation prompt before clearing all place cache',
  })
  const allCacheClearedAlert = intl.formatMessage({
    id: 't5puZ4NYeR168jN765j_',
    defaultMessage: 'All place cache cleared',
    description: 'Admin alert after clearing all place cache in admin tools',
  })
  const adminControlsCaption = intl.formatMessage({
    id: 'Tba9ECRZLwDdaBvIEUqK',
    defaultMessage: 'Admin Controls',
    description: 'Caption for admin controls section in trip plan detail',
  })
  const imagesLabel = intl.formatMessage({
    id: 'WR9r2oJw9eQzVEZ9cikB',
    defaultMessage: 'Images',
    description: 'Form field label for images subsection in admin controls',
  })
  const imagePreviewAlt = intl.formatMessage({
    id: '8H9joOSr4cRLqReB9-jZ',
    defaultMessage: 'Preview',
    description: 'Alt text for an image preview in the trip plan detail modal',
  })
  const imageThumbnailAlt = intl.formatMessage({
    id: 'fJxuLbk9ooeiUNhuNPiN',
    defaultMessage: 'Thumbnail',
    description: 'Alt text for an image thumbnail in the trip plan detail admin controls',
  })
  const deletingLabel = intl.formatMessage({
    id: '_BcMNvnyb945PBm49atG',
    defaultMessage: 'Deleting...',
    description: 'Button text shown while cache deletion is in progress',
  })
  const clearThisCacheLabel = intl.formatMessage({
    id: 'rzvEiu3CPE8BYUcHMXNZ',
    defaultMessage: 'Clear cache for this place',
    description: 'Admin button to clear cached data for a specific place',
  })
  const clearAllCacheLabel = intl.formatMessage({
    id: 'mZqrG60y2LKcEFXIX3Hj',
    defaultMessage: 'Clear all place cache',
    description: 'Admin button to clear all cached place data in admin tools',
  })

  const formatSeasonWeather = useCallback((minTemp: number, maxTemp: number, weather: string) => {
    return intl.formatMessage(
      {
        id: 'tPJwyH7YEk9ZRyrVnED-',
        defaultMessage: '{minTemp}°C – {maxTemp}°C Avg. {weather}',
        description: 'Temperature range and average weather for a season in best time to visit table',
      },
      { minTemp, maxTemp, weather },
    )
  }, [intl])

  // Fetch all sections in parallel - each loads independently
  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useGetPlaceOverview(googlePlaceId)

  const {
    data: bestTimeToVisit,
    isLoading: isBestTimeLoading,
    isError: isBestTimeError,
    refetch: refetchBestTime,
  } = useGetPlaceBestTimeToVisit(googlePlaceId)

  const {
    data: whatToLove,
    isLoading: isWhatToLoveLoading,
    isError: isWhatToLoveError,
    refetch: refetchWhatToLove,
  } = useGetPlaceWhatToLove(googlePlaceId)

  const {
    data: areas,
    isLoading: isAreasLoading,
    isError: isAreasError,
    refetch: refetchAreas,
  } = useGetPlaceAreas(googlePlaceId)

  const {
    data: insiderTips,
    isLoading: isTipsLoading,
    isError: isTipsError,
    refetch: refetchTips,
  } = useGetPlaceInsiderTips(googlePlaceId)

  const isAdmin = useAppSelector(isLEStaffOrAdmin)
  const [isDeletingThis, setIsDeletingThis] = useState(false)
  const [isDeletingAll, setIsDeletingAll] = useState(false)
  const showModal = useContext(ModalContext)

  // Show error only if all sections failed
  const isAllError = isOverviewError && isBestTimeError && isWhatToLoveError && isAreasError && isTipsError

  const handleImageClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const imageId = e.currentTarget.dataset.imageId
    if (imageId) {
      const isUrl = imageId.startsWith('http')
      const imageContent = isUrl
        ? <ModalUrlImage src={imageId} alt={imagePreviewAlt} />
        : <Image id={imageId} width={1200} quality="best" objectFit="contain" fit="center" />
      showModal(<Modal title={imagePreviewTitle} dismissible size="L">{imageContent}</Modal>)
    }
  }, [showModal, imagePreviewTitle, imagePreviewAlt])

  const handleRetry = useCallback(() => {
    refetchOverview()
    refetchBestTime()
    refetchWhatToLove()
    refetchAreas()
    refetchTips()
  }, [refetchOverview, refetchBestTime, refetchWhatToLove, refetchAreas, refetchTips])

  const handleDeleteThisCache = useCallback(async() => {
    setIsDeletingThis(true)
    try {
      await deletePlaceCache(googlePlaceId)
      handleRetry()
      alert(cacheClearedForPlaceAlert)
    } finally {
      setIsDeletingThis(false)
    }
  }, [googlePlaceId, handleRetry, cacheClearedForPlaceAlert])

  const handleDeleteAllCache = useCallback(async() => {
    if (!window.confirm(confirmDeleteAllCacheText)) {
      return
    }
    setIsDeletingAll(true)
    try {
      await deletePlaceCache()
      handleRetry()
      alert(allCacheClearedAlert)
    } finally {
      setIsDeletingAll(false)
    }
  }, [handleRetry, confirmDeleteAllCacheText, allCacheClearedAlert])

  const mapMarkers = useMemo((): Array<ChatMapMarker> => {
    if (!overview) {
      return []
    }

    const markers: Array<ChatMapMarker> = [
      {
        placeId: googlePlaceId,
        lat: overview.lat,
        lng: overview.lng,
        name: placeName,
        label: placeName,
        variant: 'primary',
      },
    ]

    if (areas?.areas) {
      areas.areas.forEach((area) => {
        if (area.lat !== undefined && area.lng !== undefined) {
          markers.push({
            placeId: `area-${area.area}`,
            lat: area.lat,
            lng: area.lng,
            name: area.area,
            label: area.area,
            variant: 'secondary',
          })
        }
      })
    }

    return markers
  }, [overview, googlePlaceId, placeName, areas?.areas])

  const allGalleryImages = useMemo((): Array<App.Image> => {
    if (!overview?.imageIds) {
      return []
    }

    return overview.imageIds.map((imageId): App.Image => {
      const isUrl = imageId.startsWith('http')
      return isUrl ? { url: imageId } : { id: imageId }
    })
  }, [overview?.imageIds])

  const gridImages = useMemo(() => take(allGalleryImages, 3), [allGalleryImages])

  const handleGalleryImageClick = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    showModal(<ImageGalleryLightbox
      images={allGalleryImages}
      defaultIndex={imageIndex}
    />)
  }, [showModal, allGalleryImages])

  const openGalleryLightbox = useCallback(() => {
    showModal(<ImageGalleryLightbox
      images={allGalleryImages}
      defaultIndex={0}
    />)
  }, [showModal, allGalleryImages])

  const handleDeleteStop = useCallback(async() => {
    if (stopId !== undefined && onDeleteStop) {
      const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
      if (!confirmed) {
        return
      }
      onDeleteStop(stopId)
      onDismiss()
    }
  }, [stopId, onDeleteStop, onDismiss, showModal, placeName])

  const showDeleteOption = stopId !== undefined && onDeleteStop !== undefined

  const askAIFooter = (
    <DrawerAskAIFooter
      contextLabel={placeName}
      isOpen={isOpen}
    />
  )

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onDismiss}
      onAfterClose={onAfterClose}
      direction="right-to-left"
      onDelete={showDeleteOption ? handleDeleteStop : undefined}
      footer={askAIFooter}
    >
      <Group direction="vertical" horizontalAlign="center">
        <ContentWrapper direction="vertical" gap={24}>
          {/* Show error only if ALL sections failed */}
          {isAllError && (
            <Group direction="vertical" gap={8} horizontalAlign="center" verticalAlign="center">
              <BodyText variant="medium" colour="critical">
                {failedToLoadText}
              </BodyText>
              <TextButton kind="secondary" size="small" onClick={handleRetry}>
                {retryLabel}
              </TextButton>
            </Group>
          )}

          {/* Header - show immediately with placeholder */}
          <Group direction="vertical" gap={4}>
            {isOverviewLoading && (
              <>
                <LoadingBox height={36} width={280} borderRadius="S" />
                <LoadingBox height={20} width={160} borderRadius="S" />
              </>
            )}
            {!isOverviewLoading && (
              <>
                <Heading variant="heading2">{overview?.locationName ?? placeName}</Heading>
                {countryName && countryName !== placeName && <BodyText variant="medium">{cityInCountryText}</BodyText>}
              </>
            )}
          </Group>

          {/* Image Gallery Grid - from overview */}
          {isOverviewLoading && (
            <LoadingBox height={280} borderRadius="L" style={{ width: '100%' }} />
          )}
          {!isOverviewLoading && gridImages.length > 0 && (
            <GridContainer>
              {gridImages.map((image, imageIndex) => (
                <GridImageClickable
                  key={image.id ?? image.url}
                  onClick={handleGalleryImageClick}
                  data-index={imageIndex}
                >
                  {image.id && <ResponsiveImage
                    id={image.id}
                    fit="center"
                    {...(imageIndex === 0 ? mainImageProps : defaultImageProps)}
                  />}
                  {image.url && <Image
                    image={image}
                    fit="center"
                  />}
                </GridImageClickable>
              ))}
              {allGalleryImages.length > 1 && (
                <GalleryButtons direction="horizontal" gap={8} horizontalAlign="end">
                  <TextButton
                    kind="secondary"
                    variant="dark"
                    startIcon={<LineImagesIcon />}
                    onClick={openGalleryLightbox}
                  >
                    {viewGalleryLabel}
                  </TextButton>
                </GalleryButtons>
              )}
            </GridContainer>
          )}

          {/* Overview Section */}
          {isOverviewLoading && (
            <Group direction="vertical" gap={8}>
              <LoadingBox height={20} borderRadius="S" style={{ width: '100%' }} />
              <LoadingBox height={20} borderRadius="S" style={{ width: '90%' }} />
              <LoadingBox height={20} width={200} borderRadius="S" />
            </Group>
          )}
          {!isOverviewLoading && overview?.overview && (
            <Group direction="vertical" gap={8}>
              <BodyText variant="large" weight="bold">
                {overview.overview.tagline}
              </BodyText>
              <BodyText variant="medium">
                {overview.overview.details}
              </BodyText>
            </Group>
          )}

          <Divider kind="primary" />

          {/* Best Time to Visit Section */}
          {isBestTimeLoading && (
            <Group direction="vertical" gap={16}>
              <LoadingBox height={24} width={160} borderRadius="S" />
              <LoadingBox height={20} borderRadius="S" style={{ width: '100%' }} />
              <LoadingBox height={150} borderRadius="L" style={{ width: '100%' }} />
            </Group>
          )}
          {!isBestTimeLoading && bestTimeToVisit?.bestTimeToVisit && (
            <Group direction="vertical" gap={16}>
              <Heading variant="heading5">{bestTimeToVisitHeading}</Heading>
              <BodyText variant="medium">{bestTimeToVisit.bestTimeToVisit.summary}</BodyText>
              <RoundedTable>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell width="33%">{seasonHeader}</TableHeaderCell>
                    <TableHeaderCell width="33%">{weatherHeader}</TableHeaderCell>
                    <TableHeaderCell width="33%">{whyVisitHeader}</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {bestTimeToVisit.bestTimeToVisit.seasons.map((season) => (
                    <TableBodyRow key={season.name}>
                      <TableBodyCell width="33%">
                        <BodyText variant="medium" weight="bold">{season.emoji} {season.name}</BodyText>
                        <Caption variant="large" colour="neutral-two">{season.months}</Caption>
                      </TableBodyCell>
                      <TableBodyCell width="33%">
                        {formatSeasonWeather(season.typicalMinTempC, season.typicalMaxTempC, season.weather)}
                      </TableBodyCell>
                      <TableBodyCell width="33%">{season.whyVisit}</TableBodyCell>
                    </TableBodyRow>
                  ))}
                </TableBody>
              </RoundedTable>
            </Group>
          )}

          <Divider kind="primary" />

          {/* What to Love Section */}
          {isWhatToLoveLoading && (
            <Group direction="vertical" gap={16}>
              <LoadingBox height={24} width={120} borderRadius="S" />
              <Group direction="vertical" gap={8}>
                <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
              </Group>
            </Group>
          )}
          {!isWhatToLoveLoading && whatToLove?.whatToLove && whatToLove.whatToLove.length > 0 && (
            <Group direction="vertical" gap={16}>
              <Heading variant="heading5">{whatYoullLoveHeading}</Heading>
              <Group direction="vertical" gap={20}>
                {whatToLove.whatToLove.map((highlight) => (
                  <Group key={highlight.item} direction="horizontal" gap={12} verticalAlign="center">
                    <BodyText variant="large">{highlight.emoji}</BodyText>
                    <Group direction="vertical" gap={4}>
                      <BodyText variant="medium" weight="bold">{highlight.item}</BodyText>
                      <BodyText variant="medium">{highlight.description}</BodyText>
                    </Group>
                  </Group>
                ))}
              </Group>
            </Group>
          )}

          {/* Areas Section */}
          {isAreasLoading && (
            <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={16}>
                <LoadingBox height={24} width={140} borderRadius="S" />
                <Group direction="vertical" gap={8}>
                  <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                  <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                </Group>
              </Group>
            </>
          )}
          {!isAreasLoading && areas?.areas && areas.areas.length > 0 && (
            <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={16}>
                <Heading variant="heading5">{topAreasHeading}</Heading>
                <Group direction="vertical" gap={20}>
                  {areas.areas.map((area, index) => (
                    <Group key={area.area} direction="horizontal" gap={12} verticalAlign="center">
                      <Group direction="vertical" gap={4}>
                        <BodyText variant="medium" weight="bold">{intl.formatMessage({ id: 'o2LDDooOjsw6gA-UYSuf', defaultMessage: '{index}. {area}', description: 'Numbered area name in top areas list in the trip planner AI chat' }, { index: index + 1, area: area.area })}</BodyText>
                        <BodyText variant="medium">{area.description}</BodyText>
                      </Group>
                    </Group>
                  ))}
                </Group>
              </Group>
            </>
          )}

          {/* Insider Tips Section */}
          {isTipsLoading && (
            <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={16}>
                <LoadingBox height={24} width={120} borderRadius="S" />
                <Group direction="vertical" gap={8}>
                  <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                  <LoadingBox height={80} borderRadius="M" style={{ width: '100%' }} />
                </Group>
              </Group>
            </>
          )}
          {!isTipsLoading && insiderTips?.insiderTips && insiderTips.insiderTips.length > 0 && (
            <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={16}>
                <Heading variant="heading5">{insiderTipsHeading}</Heading>
                <Group direction="vertical" gap={8}>
                  {insiderTips.insiderTips.map((tip) => (
                    <TipCard key={tip.tip}>
                      <Group direction="vertical" gap={4}>
                        <BodyText variant="medium" weight="bold">{tip.tip}</BodyText>
                        <BodyText variant="medium">{tip.description}</BodyText>
                      </Group>
                    </TipCard>
                  ))}
                </Group>
              </Group>
            </>
          )}

          <Divider kind="primary" />

          {/* Location Map */}
          <Heading variant="heading5">{locationHeading}</Heading>

          {isOverviewLoading && (
            <LoadingBox height={300} borderRadius="L" style={{ width: '100%' }} />
          )}
          {!isOverviewLoading && overview && (
            <MapWrapper>
              <ChatMap
                markers={mapMarkers}
                initialCenterPlaceId={googlePlaceId}
                scrollZoom={false}
              />
            </MapWrapper>
          )}

          {/* Admin Controls */}
          {isAdmin && (
            <>
              <Divider kind="primary" />
              <Group direction="vertical" gap={12}>
                <Caption variant="large" colour="neutral-two">{adminControlsCaption}</Caption>
                {overview?.imageIds && overview.imageIds.length > 0 && (
                  <Group direction="vertical" gap={8}>
                    <BodyText variant="medium" weight="bold">{imagesLabel}</BodyText>
                    <Group direction="horizontal" gap={8}>
                      {overview.imageIds.map((imageId) => {
                        const isUrl = imageId.startsWith('http')
                        return (
                          <ImageThumbnail key={imageId} data-image-id={imageId} onClick={handleImageClick}>
                            {isUrl && <UrlImage src={imageId} alt={imageThumbnailAlt} />}
                            {!isUrl && <Image id={imageId} objectFit="cover" fit="center" width={160} height={160} quality="good" />}
                          </ImageThumbnail>
                        )
                      })}
                    </Group>
                  </Group>
                )}
                <Group direction="horizontal" gap={8}>
                  <TextButton
                    kind="tertiary"
                    size="small"
                    onClick={handleDeleteThisCache}
                    disabled={isDeletingThis || isDeletingAll}
                  >
                    {isDeletingThis ? deletingLabel : clearThisCacheLabel}
                  </TextButton>
                  <TextButton
                    kind="tertiary"
                    size="small"
                    onClick={handleDeleteAllCache}
                    disabled={isDeletingThis || isDeletingAll}
                  >
                    {isDeletingAll ? deletingLabel : clearAllCacheLabel}
                  </TextButton>
                </Group>
              </Group>
            </>
          )}

        </ContentWrapper>
      </Group>
    </TripDrawer>
  )
}

export default TripPlanDetailDrawer
